import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink, useSearchParams, useLocation } from "react-router-dom";
import {
  Box,
  Flex,
  HStack,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Badge,
  Text,
  Spinner,
  useToast,
  useDisclosure,
  VStack,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from "@chakra-ui/react";
import { CalendarCheck, CheckCircle, CreditCard, Database, Plug, Menu as MenuIcon, Settings as SettingsIcon, RefreshCw, Sparkles, Rocket, Zap, Search, Upload } from "lucide-react";
import { useJobberSyncStatus } from "../../hooks/useJobberSyncStatus";

export default function Navbar({ user }) {
  const { isOpen: isMobileMenuOpen, onOpen: onMobileMenuOpen, onClose: onMobileMenuClose } = useDisclosure();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { status, refetch } = useJobberSyncStatus();
  const toast = useToast();
  const [onboardingIncomplete, setOnboardingIncomplete] = useState(false);
  const [onboardingLoaded, setOnboardingLoaded] = useState(false);
  const [resyncStarting, setResyncStarting] = useState(false);
  const previousSyncStatusRef = useRef(null);
  const hasBusiness = !!status?.business?.external_account_id || !!user?.business_id;
  const connectedProvider = status?.connected_provider || status?.business?.provider || user?.connected_provider || null;
  const providerConnected = status?.provider_connected ?? user?.provider_connected ?? false;
  const providerLabel = connectedProvider === "service_m8" ? "ServiceM8" : "Jobber";
  const reconnectHref = connectedProvider === "service_m8" ? "/oauth/service_m8/start" : "/jobber/oauth/authorize";
  const needsReconnect = !!status?.needs_reauth || (hasBusiness && status?.business?.connected === false);
  const displaySyncStatus = status?.business?.display_sync_status || null;
  const isSyncing = displaySyncStatus === "in_progress";
  const currentPhase = status?.business?.sync_state_breakdown?.current_phase;
  const billing = user?.billing;
  const trialDaysRemaining = billing?.trial_days_remaining;
  const showTrialPill = billing?.status === "trialing" && !billing?.billing_exempt;
  const trialPillText = Number.isInteger(trialDaysRemaining)
    ? `Trial: ${trialDaysRemaining} ${trialDaysRemaining === 1 ? "day" : "days"} left`
    : "Trial active";

  const isActive = (path) => {
    if (path === "/schedule") {
      return location.pathname === "/schedule" || location.pathname.startsWith("/schedule/");
    }
    return location.pathname === path;
  };

  // Refetch status after OAuth reconnection
  useEffect(() => {
    if (searchParams.get("reconnected") === "true") {
      // Refetch immediately
      refetch();

      // Refetch again after 1 second to ensure DB is updated
      const timer = setTimeout(() => {
        refetch();
      }, 1000);

      // Clean up the URL parameter
      searchParams.delete("reconnected");
      setSearchParams(searchParams, { replace: true });

      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams, refetch]);

  useEffect(() => {
    if (isSyncing) {
      setResyncStarting(false);
    }
  }, [isSyncing]);

  useEffect(() => {
    const previousStatus = previousSyncStatusRef.current;
    const nextStatus = displaySyncStatus;

    if (previousStatus === "in_progress" && nextStatus === "completed") {
      toast({
        title: `${providerLabel} sync complete`,
        description: `Your ${providerLabel} data is up to date.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    }

    if (previousStatus === "in_progress" && nextStatus === "failed") {
      toast({
        title: `${providerLabel} sync failed`,
        description: needsReconnect
          ? `Reconnect ${providerLabel} to resume syncing.`
          : "The sync did not finish successfully.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }

    previousSyncStatusRef.current = nextStatus;
  }, [displaySyncStatus, needsReconnect, providerLabel, toast]);

  useEffect(() => {
    const fetchOnboardingProgress = async () => {
      try {
        const response = await fetch("/api/onboarding_progress");
        const data = await response.json();
        if (!response.ok || !data?.success) return;

        const steps = Array.isArray(data.steps) ? data.steps : [];
        setOnboardingIncomplete(steps.some((step) => !step.complete));
        setOnboardingLoaded(true);
      } catch (error) {
        // Keep navbar stable if endpoint is unavailable.
      }
    };

    fetchOnboardingProgress();

    const handleRefresh = () => fetchOnboardingProgress();
    window.addEventListener("onboarding-progress-refresh", handleRefresh);
    return () => window.removeEventListener("onboarding-progress-refresh", handleRefresh);
  }, [location.pathname]);

  const handleResync = async () => {
    try {
      setResyncStarting(true);
      const response = await fetch("/api/jobber_status/resync", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
      });

      if (response.ok) {
        toast({
          title: `${providerLabel} resync started`,
          description: `We’re syncing your ${providerLabel} data now. You can keep using the app while this runs.`,
          status: "success",
          duration: 3500,
          isClosable: true,
        });
        refetch();
      } else {
        setResyncStarting(false);
      }
    } catch (error) {
      setResyncStarting(false);
      toast({
        title: `Failed to start ${providerLabel} sync`,
        description: error?.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error("Resync error:", error);
    }
  };

  const handleSignOut = (e) => {
    e.preventDefault();
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    fetch("/users/sign_out", {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
    }).then(() => {
      window.location.href = "/users/sign_in";
    });
  };

  const handleDisconnectProvider = async () => {
    const confirmed = window.confirm(`Disconnect ${providerLabel}? You will need to reconnect before syncing or using ${providerLabel}-backed features again.`);
    if (!confirmed) return;

    try {
      const response = await fetch("/api/provider_connection/disconnect", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name=\"csrf-token\"]').content,
        },
        body: JSON.stringify({ provider: connectedProvider }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Failed to disconnect ${providerLabel}`);
      }

      toast({
        title: `${providerLabel} disconnected`,
        description: `Your ${providerLabel} connection has been removed.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      refetch();
    } catch (error) {
      toast({
        title: `Failed to disconnect ${providerLabel}`,
        description: error?.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box as="nav" bg="white" boxShadow="sm" borderBottom="1px" borderColor="gray.200">
      <Box mx="auto" px={{ base: 3, sm: 4 }}>
        <Flex justify="space-between" align="center" h={16}>
          <HStack spacing={{ base: 2, md: 3 }} minW={0}>
            <Flex as={RouterLink} to="/" align="center" gap={2} _hover={{ textDecoration: "none" }}>
              <CalendarCheck size={24} color="var(--chakra-colors-blue-500)" />
              <Text fontSize={{ base: "md", sm: "xl" }} fontWeight="bold" noOfLines={1}>Same Day Openings</Text>
            </Flex>
            {onboardingLoaded && onboardingIncomplete && (
              <Button
                as={RouterLink}
                to="/onboarding"
                size="xs"
                display={{ base: "none", sm: "inline-flex" }}
                colorScheme={location.pathname.startsWith("/onboarding") ? "blue" : "orange"}
                variant={location.pathname.startsWith("/onboarding") ? "solid" : "outline"}
              >
                Onboarding
              </Button>
            )}
            {(isSyncing || resyncStarting || needsReconnect) && (
              <Badge
                as={needsReconnect ? "a" : undefined}
                href={needsReconnect ? reconnectHref : undefined}
                colorScheme={needsReconnect ? "red" : "blue"}
                variant={needsReconnect ? "solid" : "subtle"}
                px={3}
                py={1}
                borderRadius="full"
                alignItems="center"
                gap={2}
                _hover={needsReconnect ? { textDecoration: "none", opacity: 0.9 } : undefined}
                cursor={needsReconnect ? "pointer" : "default"}
                display={{ base: "none", lg: "flex" }}
              >
                {(isSyncing || resyncStarting) && !needsReconnect && <Spinner size="xs" />}
                <Text fontSize="xs">
                  {needsReconnect
                    ? `${providerLabel} reconnect required`
                    : resyncStarting
                      ? `Starting ${providerLabel} sync...`
                      : currentPhase || `${providerLabel} sync in progress`}
                </Text>
              </Badge>
            )}
            {showTrialPill && (
              <Button
                as={RouterLink}
                to="/billing"
                size="xs"
                colorScheme="green"
                variant={isActive("/billing") ? "solid" : "outline"}
                leftIcon={<CreditCard size={13} />}
                display={{ base: "none", xl: "inline-flex" }}
              >
                {trialPillText}
              </Button>
            )}
          </HStack>

          {/* Desktop menu */}
          <HStack spacing={1} display={{ base: "none", md: "flex" }}>
            {/* Smart Schedule Finder */}
            <Button
              as={RouterLink}
              to="/schedule"
              variant="ghost"
              size="sm"
              leftIcon={<Zap size={16} />}
              color={isActive("/schedule") ? "blue.600" : undefined}
              borderBottom={isActive("/schedule") ? "2px solid" : undefined}
              borderColor={isActive("/schedule") ? "blue.600" : undefined}
              borderRadius={isActive("/schedule") ? "md" : "md"}
              borderBottomRadius={isActive("/schedule") ? "0" : undefined}
              fontWeight={isActive("/schedule") ? "semibold" : "normal"}
            >
              Smart Schedule Finder
            </Button>

            {/* Waitlist Autopilot */}
            <Button
              as={RouterLink}
              to="/waitlist"
              variant="ghost"
              size="sm"
              leftIcon={<Sparkles size={16} />}
              color={isActive("/waitlist") ? "blue.600" : undefined}
              borderBottom={isActive("/waitlist") ? "2px solid" : undefined}
              borderColor={isActive("/waitlist") ? "blue.600" : undefined}
              borderRadius={isActive("/waitlist") ? "md" : "md"}
              borderBottomRadius={isActive("/waitlist") ? "0" : undefined}
              fontWeight={isActive("/waitlist") ? "semibold" : "normal"}
            >
              Waitlist Autopilot
            </Button>

            {/* Fast Book Banner */}
            <Button
              as={RouterLink}
              to="/embed"
              variant="ghost"
              size="sm"
              leftIcon={<Rocket size={16} />}
              color={isActive("/embed") ? "blue.600" : undefined}
              borderBottom={isActive("/embed") ? "2px solid" : undefined}
              borderColor={isActive("/embed") ? "blue.600" : undefined}
              borderRadius={isActive("/embed") ? "md" : "md"}
              borderBottomRadius={isActive("/embed") ? "0" : undefined}
              fontWeight={isActive("/embed") ? "semibold" : "normal"}
            >
              Fast Book Banner
            </Button>

            {/* Settings & User Email */}
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                size="sm"
                leftIcon={<SettingsIcon size={16} />}
                rightIcon={null}
              >
                {user?.email}
              </MenuButton>
              <MenuList>
                {user?.admin && (
                  <MenuItem as={RouterLink} to="/leads" icon={<Search size={16} />}>
                    Leads
                  </MenuItem>
                )}
                {user?.admin && (
                  <MenuItem as={RouterLink} to="/google-places" icon={<Search size={16} />}>
                    Google Places
                  </MenuItem>
                )}
                {user?.admin && (
                  <MenuItem as={RouterLink} to="/lead-imports" icon={<Upload size={16} />}>
                    CSV Lead Imports
                  </MenuItem>
                )}

                {user?.admin && <MenuDivider />}

                {providerConnected && (
                  <MenuItem
                    onClick={handleResync}
                    isDisabled={isSyncing || resyncStarting}
                    icon={<RefreshCw size={16} />}
                  >
                    {resyncStarting ? "Starting sync..." : isSyncing ? "Sync in progress" : `Resync ${providerLabel}`}
                  </MenuItem>
                )}

                {providerConnected && !needsReconnect && (
                  <MenuItem
                    onClick={handleDisconnectProvider}
                    icon={<Plug size={16} />}
                    color="yellow.700"
                    _hover={{ bg: "yellow.50" }}
                    _focus={{ bg: "yellow.50" }}
                  >
                    Disconnect {providerLabel}
                  </MenuItem>
                )}

                <MenuDivider />

                {/* Jobber Connection Status */}
                {hasBusiness ? (
                  needsReconnect ? (
                    <MenuItem as="a" href={reconnectHref} color="red.600" icon={<Plug size={16} />}>
                      Reconnect to {providerLabel}
                    </MenuItem>
                  ) : providerConnected ? (
                    <Box px={3} py={2}>
                      <Badge colorScheme="green" display="flex" alignItems="center" gap={1} w="fit-content">
                        <CheckCircle size={12} />
                        Connected to {providerLabel}
                      </Badge>
                    </Box>
                  ) : (
                    <MenuItem as="a" href="/jobber/oauth/authorize" icon={<Plug size={16} />}>
                      Connect to Jobber
                    </MenuItem>
                  )
                ) : (
                  <MenuItem as="a" href="/jobber/oauth/authorize" icon={<Plug size={16} />}>
                    Connect to Jobber
                  </MenuItem>
                )}

                <MenuDivider />

                <MenuItem as={RouterLink} to="/availability" icon={<CalendarCheck size={16} />}>
                  Availability
                </MenuItem>

                <MenuDivider />

                <MenuItem as={RouterLink} to="/data-status" icon={<Database size={16} />}>
                  Data Status
                </MenuItem>

                <MenuItem as={RouterLink} to="/billing" icon={<CreditCard size={16} />}>
                  Billing
                </MenuItem>

                <MenuDivider />

                <MenuItem onClick={handleSignOut}>
                  Sign out
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>

          {/* Mobile menu button */}
          <IconButton
            display={{ base: "flex", md: "none" }}
            icon={<MenuIcon size={24} />}
            variant="ghost"
            aria-label="Open menu"
            onClick={onMobileMenuOpen}
            minW="44px"
            minH="44px"
          />
        </Flex>

      </Box>

      {/* Mobile menu drawer */}
      <Drawer isOpen={isMobileMenuOpen} placement="right" onClose={onMobileMenuClose}>
        <DrawerOverlay />
        <DrawerContent maxW={{ base: "calc(100vw - 24px)", sm: "360px" }}>
          <DrawerCloseButton />
          <DrawerHeader>Menu</DrawerHeader>

          <DrawerBody>
            <VStack spacing={2} align="stretch">
              {onboardingLoaded && onboardingIncomplete && (
                <Button
                  as={RouterLink}
                  to="/onboarding"
                  colorScheme="orange"
                  variant="outline"
                  justifyContent="flex-start"
                  onClick={onMobileMenuClose}
                  minH="44px"
                >
                  Continue Onboarding
                </Button>
              )}
              {(isSyncing || resyncStarting || needsReconnect) && (
                <Box px={3} py={2} borderRadius="md" bg={needsReconnect ? "red.50" : "blue.50"}>
                  <HStack spacing={2}>
                    {(isSyncing || resyncStarting) && !needsReconnect && <Spinner size="xs" />}
                    <Text fontSize="sm" color={needsReconnect ? "red.700" : "blue.700"}>
                      {needsReconnect ? `${providerLabel} reconnect required` : resyncStarting ? `Starting ${providerLabel} sync...` : currentPhase || `${providerLabel} sync in progress`}
                    </Text>
                  </HStack>
                </Box>
              )}
              {/* Main Menu Items */}
              <Button
                as={RouterLink}
                to="/schedule"
                variant="ghost"
                justifyContent="flex-start"
                leftIcon={<Zap size={16} />}
                color={isActive("/schedule") ? "blue.600" : undefined}
                bg={isActive("/schedule") ? "blue.50" : undefined}
                fontWeight={isActive("/schedule") ? "semibold" : "normal"}
                onClick={onMobileMenuClose}
              >
                Smart Schedule Finder
              </Button>
              <Button
                as={RouterLink}
                to="/waitlist"
                variant="ghost"
                justifyContent="flex-start"
                leftIcon={<Sparkles size={16} />}
                color={isActive("/waitlist") ? "blue.600" : undefined}
                bg={isActive("/waitlist") ? "blue.50" : undefined}
                fontWeight={isActive("/waitlist") ? "semibold" : "normal"}
                onClick={onMobileMenuClose}
              >
                Waitlist Autopilot
              </Button>
              <Button
                as={RouterLink}
                to="/embed"
                variant="ghost"
                justifyContent="flex-start"
                leftIcon={<Rocket size={16} />}
                color={isActive("/embed") ? "blue.600" : undefined}
                bg={isActive("/embed") ? "blue.50" : undefined}
                fontWeight={isActive("/embed") ? "semibold" : "normal"}
                onClick={onMobileMenuClose}
              >
                Fast Book Banner
              </Button>

              {user?.admin && (
                <>
                  <Button
                    as={RouterLink}
                    to="/leads"
                    variant="ghost"
                    justifyContent="flex-start"
                    leftIcon={<Search size={16} />}
                    color={isActive("/leads") ? "blue.600" : undefined}
                    bg={isActive("/leads") ? "blue.50" : undefined}
                    fontWeight={isActive("/leads") ? "semibold" : "normal"}
                    onClick={onMobileMenuClose}
                  >
                    Leads
                  </Button>
                  <Button
                    as={RouterLink}
                    to="/google-places"
                    variant="ghost"
                    justifyContent="flex-start"
                    leftIcon={<Search size={16} />}
                    color={isActive("/google-places") ? "blue.600" : undefined}
                    bg={isActive("/google-places") ? "blue.50" : undefined}
                    fontWeight={isActive("/google-places") ? "semibold" : "normal"}
                    onClick={onMobileMenuClose}
                  >
                    Google Places
                  </Button>
                  <Button
                    as={RouterLink}
                    to="/lead-imports"
                    variant="ghost"
                    justifyContent="flex-start"
                    leftIcon={<Upload size={16} />}
                    color={isActive("/lead-imports") ? "blue.600" : undefined}
                    bg={isActive("/lead-imports") ? "blue.50" : undefined}
                    fontWeight={isActive("/lead-imports") ? "semibold" : "normal"}
                    onClick={onMobileMenuClose}
                  >
                    CSV Lead Imports
                  </Button>
                </>
              )}

              <Box pt={4} borderTop="1px" borderColor="gray.200" mt={2}>
                <Text fontSize="xs" color="gray.500" px={3} mb={2}>Settings</Text>

                {showTrialPill && (
                  <Button
                    as={RouterLink}
                    to="/billing"
                    colorScheme="green"
                    variant={isActive("/billing") ? "solid" : "outline"}
                    size="sm"
                    w="full"
                    justifyContent="flex-start"
                    leftIcon={<CreditCard size={16} />}
                    onClick={onMobileMenuClose}
                    mb={2}
                  >
                    {trialPillText}
                  </Button>
                )}

                {providerConnected && (
                  <Button
                    variant="ghost"
                    justifyContent="flex-start"
                    leftIcon={<RefreshCw size={16} />}
                    onClick={() => {
                      handleResync();
                      onMobileMenuClose();
                    }}
                    isDisabled={isSyncing || resyncStarting}
                  >
                    {resyncStarting ? "Starting sync..." : isSyncing ? "Sync in progress" : `Resync ${providerLabel}`}
                  </Button>
                )}

                {/* Jobber Connection */}
                <Box px={3} py={2}>
                  {hasBusiness ? (
                    needsReconnect ? (
                      <Button
                        as="a"
                        href={reconnectHref}
                        colorScheme="red"
                        size="sm"
                        w="full"
                        leftIcon={<Plug size={16} />}
                      >
                        Reconnect to {providerLabel}
                      </Button>
                    ) : providerConnected ? (
                      <VStack align="stretch" spacing={2}>
                        <Badge colorScheme="green" display="flex" alignItems="center" gap={1} w="fit-content">
                          <CheckCircle size={12} />
                          Connected to {providerLabel}
                        </Badge>
                        <Button
                          colorScheme="yellow"
                          variant="outline"
                          size="sm"
                          w="full"
                          leftIcon={<Plug size={16} />}
                          onClick={() => {
                            handleDisconnectProvider();
                            onMobileMenuClose();
                          }}
                        >
                          Disconnect {providerLabel}
                        </Button>
                      </VStack>
                    ) : (
                      <Button
                        as="a"
                        href="/jobber/oauth/authorize"
                        colorScheme="blue"
                        size="sm"
                        w="full"
                        leftIcon={<Plug size={16} />}
                      >
                        Connect to Jobber
                      </Button>
                    )
                  ) : (
                    <Button
                      as="a"
                      href="/jobber/oauth/authorize"
                      colorScheme="blue"
                      size="sm"
                      w="full"
                      leftIcon={<Plug size={16} />}
                    >
                      Connect to Jobber
                    </Button>
                  )}
                </Box>

                <Button
                  as={RouterLink}
                  to="/availability"
                  variant="ghost"
                  justifyContent="flex-start"
                  leftIcon={<CalendarCheck size={16} />}
                  onClick={onMobileMenuClose}
                >
                  Availability
                </Button>

                <Button
                  as={RouterLink}
                  to="/data-status"
                  variant="ghost"
                  justifyContent="flex-start"
                  leftIcon={<Database size={16} />}
                  onClick={onMobileMenuClose}
                >
                  Data Status
                </Button>

                <Button
                  as={RouterLink}
                  to="/billing"
                  variant="ghost"
                  justifyContent="flex-start"
                  leftIcon={<CreditCard size={16} />}
                  onClick={onMobileMenuClose}
                >
                  Billing
                </Button>
              </Box>

              <Box pt={4} borderTop="1px" borderColor="gray.200" mt={2}>
                <Text fontSize="sm" color="gray.600" px={3} mb={2}>{user?.email}</Text>
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  onClick={(e) => {
                    handleSignOut(e);
                    onMobileMenuClose();
                  }}
                >
                  Sign out
                </Button>
              </Box>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
